<template>
  <div
    v-if="device"
    class="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
    @mousedown.self="$emit('close')">
    <!-- The sheet (card t_488df70b): the chrome owns the surface, the head, the
         accent ring and the alarm flash; this modal owns the overlay, the size
         and the one scrolling region below. -->
    <DeviceSheetChrome
      variant="panel"
      class="w-full max-w-2xl mx-4 max-h-[90vh]"
      :name="device.deviceName"
      :room="roomName ?? ''"
      :glyph="deviceIcon"
      :thumbnail-url="device.thumbnailUrl ?? ''"
      :accent="deviceStatusColor ?? undefined"
      :active="deviceStatusColor !== null"
      :state-text="stateText"
      :alarm-tint="hasAlarmActive"
      :motion="motionActive"
      :mono-idle="isMotionSensor"
      :is-viewer="isViewer"
      :dismissible="true"
      @navigate-room="navigateToRoom"
      @open-settings="openSettings"
      @close="$emit('close')">
      <!-- The chrome draws the room row only when `room` is set — its own slot
           content wins over the room prop, so the slot carries the device's
           room, or the word the old modal showed ("No room") when it has none. -->
      <template #room>{{ roomName ?? tt('No room', 'Brez prostora') }}</template>

      <DeviceWidget
        mode="control"
        :device="device"
        @view-stream="$emit('view-stream')"
        @view-details="$emit('view-details')"
        @delete="$emit('delete')" />
    </DeviceSheetChrome>
  </div>
</template>

<script setup lang="ts">
import { computed, toRef } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import DeviceWidget from './DeviceWidget.vue';
import DeviceSheetChrome from './device-sheet/DeviceSheetChrome.vue';
import { useEntitiesStore } from '@/stores/entities';
import { useDeviceValues } from '@/composables/useDeviceValues';
import { deviceSheetCategory, deviceSheetSynologyKind, deviceIsMotionSensor } from '../utils/deviceSheet';
import { ROBOT_BATTERY_KEY, robotAway, robotPercent, robotState, robotStateWord } from '../utils/robotState';
import { useDemoGuest } from '../composables/useDemoGuest';
import { tt } from '../utils/consoleText';
import type { Device } from '@/types';

const props = defineProps<{
  device: Device;
  integrationName: string;
}>();

const emit = defineEmits<{
  close: [];
  'open-settings': [];
  'view-stream': [];
  'view-details': [];
  delete: [];
}>();

const entitiesStore = useEntitiesStore();
/**
 * The guest (viewer) experience (card t_803e86a4): the header's gear opens the
 * device editor, which is a settings surface — a viewer keeps the control body
 * and never gets the door. The chrome reads this flag for its own gear.
 */
const { isViewer } = useDemoGuest();
const route = useRoute();
const router = useRouter();

// Subscribe to real-time device value updates so the status colour and the
// state pill react immediately
const { currentValues: liveValues, initializeValues } = useDeviceValues(toRef(() => props.device.id));

/** One value row of a device — the hub's `DeviceValue` plus the raw API spellings
 *  (`property_key`, `values`) this modal still reads. */
type DeviceValueRow = {
  valueKey?: string;
  property_key?: string;
  currentValue?: unknown;
};
if (props.device.deviceValues?.length) initializeValues(props.device.deviceValues);

/** The device's value rows, either spelling the row arrived under. */
const deviceValues = computed<DeviceValueRow[]>(
  () => props.device.deviceValues ?? (props.device as { values?: DeviceValueRow[] }).values ?? [],
);

/** The category — the one fold the sheet's head, its pill and its colour read. */
const category = computed(() => deviceSheetCategory(props.device));

/** The Synology family's kind (the body's own fold) — each kind reads its own health value. */
const synologyKind = computed(() => deviceSheetSynologyKind(props.device));

/** The live value for the first of `keys` the socket carries, else the static row's. */
function readValue(keys: string[]): unknown {
  for (const key of keys) {
    if (key in liveValues.value) return liveValues.value[key];
  }
  return deviceValues.value.find((value) => keys.includes(value.valueKey ?? ''))?.currentValue;
}

/**
 * The motion pair (owner, 24.10.2026) — read off the LIVE rows this modal already
 * subscribes, not the row it rendered with: a sensor can flip while the sheet is
 * open, and both the colour return and the breath have to follow it.
 */
const isMotionSensor = computed(() => deviceIsMotionSensor(props.device));

const motionActive = computed(() => {
  if (!isMotionSensor.value) return false;
  const value = readValue(['occupancy', 'presence', 'zone_1_presence', 'motion', 'state']);
  return value === true || value === 1 || value === 'on' || value === 'true';
});

/** The boolean datapoint's own on-reading, in every spelling the hub uses. */
function isOn(keys = ['power', 'on_off', 'state']): boolean {
  const value = readValue(keys);
  return value === true || value === 1 || value === 'on';
}

/** The boolean datapoint's own off-reading — the reverse of `isOn`. */
function isOff(keys: string[]): boolean {
  const value = readValue(keys);
  return value === false || value === 0 || value === 'false' || value === 'off';
}

/** A numeric reading, or null when the value is missing / not a number. */
function toFiniteNumber(value: unknown): number | null {
  if (value === undefined || value === null || value === '') return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

/**
 * Whether the fan is running — the widget's own fold (FanWidget's `fanMode` +
 * `isOn`): a speed-family fan (speed_bool/speed_enum, `fan_speed`) carries no
 * power key, so its running reading IS `fan_speed > 0`; the continuous and
 * on_off families keep the power keys.
 */
function fanRunning(): boolean {
  const carriesSpeed =
    'fan_speed' in liveValues.value || deviceValues.value.some((value) => (value.valueKey ?? '') === 'fan_speed');
  if (carriesSpeed) {
    const speed = toFiniteNumber(readValue(['fan_speed']));
    return speed !== null && speed > 0;
  }
  return isOn(['switch', 'power', 'on_off', 'state']);
}

const room = computed(() => (props.device.roomId ? entitiesStore.getRoom(props.device.roomId) : null));
const roomName = computed(() => room.value?.roomName ?? null);

function navigateToRoom() {
  emit('close');
  const hubId = route.params.hubId;
  const path = hubId ? `/h/${String(hubId)}/rooms/${props.device.roomId}` : `/rooms/${props.device.roomId}`;
  router.push(path);
}

/** The gear leaves the modal for the device editor — exactly the two emits it made before. */
function openSettings() {
  emit('open-settings');
  emit('close');
}

const deviceIcon = computed(() => {
  const icon = props.device.icon || props.device.deviceControl || props.device.deviceType || 'devices';
  const iconMap: Record<string, string> = {
    light: 'light_mode',
    switch: 'toggle_on',
    outlet: 'power',
    sensor: 'sensors',
    temperature: 'thermostat',
    cover: 'blinds',
    blind: 'blinds',
    shutter: 'blinds',
    window: 'window',
    lock: 'lock',
    camera: 'videocam',
    computer: 'computer',
    other: 'devices',
  };

  if (iconMap[icon]) {
    return iconMap[icon];
  }

  return props.device.icon || 'devices';
});

function getDeviceValueMeta(deviceValues: DeviceValueRow[], keys: string[]) {
  return deviceValues.find((value) => keys.includes(value.valueKey ?? ''));
}

function getMetaNumber(source: Record<string, unknown> | null | undefined, keys: string[], fallback: number) {
  for (const key of keys) {
    const raw = source?.[key];
    if (raw === undefined || raw === null || raw === '') continue;
    const numeric = Number(raw);
    if (Number.isFinite(numeric)) return numeric;
  }
  return fallback;
}

// Compute device status color matching widget border/icon colors per device type
const deviceStatusColor = computed<string | null>(() => {
  switch (category.value) {
    case 'light': {
      if (!isOn()) return null;
      const raw = readValue(['color', 'rgb']);
      if (raw) {
        if (typeof raw === 'string' && raw.startsWith('#')) return raw;
        const parts = (typeof raw === 'string' ? raw.split(',') : Array.isArray(raw) ? raw : []).map(Number);
        const [r, g, b] = parts;
        if (parts.length >= 3 && !isNaN(r) && !isNaN(g) && !isNaN(b))
          return (
            '#' +
            [r, g, b]
              .map((x) => Math.round(x).toString(16).padStart(2, '0'))
              .join('')
              .toUpperCase()
          );
      }
      return 'rgb(250, 204, 21)';
    }
    case 'switch':
      return isOn() ? 'rgb(34, 197, 94)' : null;
    case 'fan':
      // The fan's own fold, shared with the pill's text below (FanWidget's
      // `fanMode`/`isOn`): speed family → `fan_speed > 0`, the rest → their
      // power keys. Without it a running speed-family fan got no accent
      // (card t_2393f4f0).
      return fanRunning() ? 'rgb(56, 189, 248)' : null;
    case 'lock': {
      const unlockDp = deviceValues.value.find((v) => v.property_key === 'unlock');
      const locked = unlockDp ? unlockDp.currentValue === false : isOn();
      return locked ? 'rgb(34, 197, 94)' : null;
    }
    case 'cover': {
      const pos = readValue(['position_status', 'position', 'level']);
      return pos !== null && pos !== undefined && Number(pos) > 0 ? 'rgb(96, 165, 250)' : null;
    }
    case 'alarm': {
      const state = String(readValue(['alarm_state', 'arm_state', 'state']) ?? '');
      return state.startsWith('armed') || state === 'triggered' ? 'rgb(239, 68, 68)' : null;
    }
    case 'binary_sensor': {
      // If alarm is active, use red; otherwise normal purple
      if (hasAlarmActive.value) return 'rgb(220, 38, 38)';
      return isOn(['state', 'power', 'on_off', 'occupancy']) ? 'rgb(168, 85, 247)' : null;
    }
    case 'strike_lock': {
      const unlockDp = deviceValues.value.find((v) => v.property_key === 'unlock');
      const unlocked = unlockDp ? unlockDp.currentValue === true : isOn(['unlock', 'power', 'on_off', 'state']);
      return unlocked ? 'rgb(251, 191, 36)' : null;
    }
    case 'thermostat': {
      // Blue → red over device min–max (matches ThermostatWidget.tempColorRaw)
      const tDv = getDeviceValueMeta(deviceValues.value, ['effective_target', 'target_temperature', 'setpoint']);
      const tMin = getMetaNumber(tDv, ['minValue', 'min_value', 'min'], 16);
      const tMax = getMetaNumber(tDv, ['maxValue', 'max_value', 'max'], 24);
      const t = Number(readValue(['target_temperature', 'setpoint']) ?? 21);
      const pct = Math.max(0, Math.min(100, ((t - tMin) / Math.max(0.001, tMax - tMin)) * 100));
      const r = Math.round(59 + (239 - 59) * (pct / 100));
      const g = Math.round(130 + (68 - 130) * (pct / 100));
      const b = Math.round(246 + (68 - 246) * (pct / 100));
      return `rgb(${r},${g},${b})`;
    }
    case 'climate': {
      // Blue → amber → red over device min–max (matches ClimateWidget.tempColorRaw)
      const tDv = getDeviceValueMeta(deviceValues.value, ['effective_target', 'target_temperature', 'setpoint']);
      const tMin = getMetaNumber(tDv, ['minValue', 'min_value', 'min'], 7);
      const tMax = getMetaNumber(tDv, ['maxValue', 'max_value', 'max'], 35);
      const t = Number(readValue(['effective_target', 'target_temperature', 'setpoint']) ?? 20);
      const p = Math.max(0, Math.min(1, (t - tMin) / Math.max(0.001, tMax - tMin)));
      let r: number, g: number, b: number;
      if (p <= 0.4) {
        const t2 = p / 0.4;
        r = Math.round(59 + (245 - 59) * t2);
        g = Math.round(130 + (158 - 130) * t2);
        b = Math.round(246 + (11 - 246) * t2);
      } else {
        const t2 = (p - 0.4) / 0.6;
        r = Math.round(245 + (239 - 245) * t2);
        g = Math.round(158 + (68 - 158) * t2);
        b = Math.round(11 + (68 - 11) * t2);
      }
      return `rgb(${r},${g},${b})`;
    }
    case 'computer': {
      // Green when the machine is online (device value 'online').
      return isOn(['online']) ? 'rgb(34, 197, 94)' : null;
    }
    case 'presence': {
      // The tracked UniFi client (`presence`, P10's family): its own `present`
      // — the reading its body's tile shows — green while present, no tone
      // while away.
      return isOn(['present', 'presence', 'zone_1_presence']) ? 'rgb(34, 197, 94)' : null;
    }
    case 'synology': {
      // The family's own health fold (SynologyWidget's): the NAS its `online`,
      // a volume its `volume_error`, a disk its `disk_error`/`smart_ok` (the
      // driver mirrors the two — either spelling reports the same failure).
      // An errored unit tints the body's alarm tone, a healthy one its meter
      // green. `disk_full` and `dsm_update_available` are the family's caution
      // flags (hub level: warning, not error) — they keep the healthy tone and
      // their words ride the body's pills / notice.
      if (synologyKind.value === 'nas') return isOn(['online']) ? 'rgb(34, 197, 94)' : null;
      if (synologyKind.value === 'volume') return isOn(['volume_error']) ? 'rgb(239, 68, 68)' : 'rgb(34, 197, 94)';
      return isOn(['disk_error']) || isOff(['smart_ok']) ? 'rgb(239, 68, 68)' : 'rgb(34, 197, 94)';
    }
    case 'vacuum':
    case 'lawn_mower': {
      // The two robots (card t_390bc438): their own RVC state decides. Away
      // from the dock the head wears the family's accent (the sheet's own
      // token), an error wears the alarm tone, and a machine resting on its
      // dock carries no tone at all.
      const current = robotState(deviceValues.value, liveValues.value);
      if (current === 'Error') return 'rgb(239, 68, 68)';
      if (!robotAway(current)) return null;
      return category.value === 'lawn_mower' ? 'var(--device-color-mower)' : 'var(--device-color-vacuum)';
    }
    case 'heat': {
      // The heat-demand controller tints by its contact, never by its master
      // switch — the switch being on does not mean the boiler is running.
      return isOn(['heat_demand']) ? 'var(--device-color-heat)' : null;
    }
    default:
      return null;
  }
});

/**
 * The head's state pill (card t_488df70b): a short live reading per category,
 * off the values this modal already subscribes to — read-only, no request.
 * An empty answer draws no pill (a category whose live reading is not known
 * yet), never a guess.
 */
const stateText = computed<string>(() => {
  const on = tt('On', 'Vklopljeno');
  const off = tt('Off', 'Izklopljeno');

  switch (category.value) {
    case 'light': {
      if (!isOn()) return off;
      const brightness = toFiniteNumber(readValue(['brightness', 'dimming', 'level']));
      return brightness === null ? on : `${on} · ${Math.round(brightness)} %`;
    }
    case 'switch':
      return isOn(['power', 'on_off', 'state', 'switch']) ? on : off;
    case 'cover': {
      // The value is the hub's own position datapoint — the number the tile and
      // the body both show. No direction word: this hub reads position 0 as
      // open and 100 as its closed stop, so a "% open" label would state the
      // opposite of the hub's own cover state at either end.
      const position = toFiniteNumber(readValue(['position_status', 'position', 'level']));
      return position === null ? '' : `${Math.round(position)} %`;
    }
    case 'thermostat':
    case 'climate': {
      const target = toFiniteNumber(readValue(['effective_target', 'target_temperature', 'setpoint']));
      return target === null ? '' : `${Math.round(target * 10) / 10} °C`;
    }
    case 'lock': {
      const locked = readValue(['locked']);
      const engaged = locked === undefined ? isOn() : locked === true || locked === 1 || locked === 'locked';
      return engaged ? tt('Locked', 'Zaklenjeno') : tt('Unlocked', 'Odklenjeno');
    }
    case 'strike_lock': {
      const unlocked = readValue(['unlock']);
      const open =
        unlocked === undefined ? isOn(['unlock', 'power', 'on_off', 'state']) : unlocked === true || unlocked === 1;
      return open ? tt('Unlocked', 'Odklenjeno') : tt('Locked', 'Zaklenjeno');
    }
    case 'fan': {
      // The fan's own fold (FanWidget's `fanMode`/`isOn`): a speed-family fan
      // (`fan_speed`, speed_bool/speed_enum) carries no power key, and the old
      // gate — power keys only — returned `Off` before the `fan_speed` read
      // below could ever run (card t_2393f4f0). The continuous / on_off
      // families keep the power keys.
      if (!fanRunning()) return off;
      const percent = toFiniteNumber(readValue(['fan_speed_percentage']));
      if (percent !== null && percent > 0) return `${on} · ${Math.round(percent)} %`;
      const speed = toFiniteNumber(readValue(['fan_speed']));
      return speed !== null && speed > 0 ? tt(`Speed ${speed}`, `Hitrost ${speed}`) : on;
    }
    case 'media':
    case 'media_player': {
      // The device's own word when it carries one (`play_state`), else its power.
      const playState = String(readValue(['play_state']) ?? '').toLowerCase();
      if (playState === 'playing') return tt('Playing', 'Predvajanje');
      if (playState === 'paused') return tt('Paused', 'Premor');
      return isOn(['power', 'on_off', 'state']) ? on : off;
    }
    case 'presence_sensor':
      return isOn(['presence', 'zone_1_presence', 'state']) ? tt('Present', 'Prisoten') : tt('Clear', 'Prosto');
    case 'binary_sensor':
      if (hasAlarmActive.value) return tt('Triggered', 'Sprožen');
      return isOn(['state', 'power', 'on_off', 'occupancy', 'sensor_value']) ? tt('Active', 'Aktivno') : off;
    case 'alarm': {
      const state = String(readValue(['alarm_state', 'arm_state', 'state']) ?? '');
      if (state === 'triggered') return tt('Triggered', 'Sprožen');
      return state.startsWith('armed') ? tt('Armed', 'Aktiviran') : off;
    }
    case 'computer':
      return isOn(['online']) ? on : off;
    case 'presence':
      // The tracked UniFi client: its own `present` (the body's tile reading);
      // Present/Clear is the presence family's chrome pair (presence_sensor
      // ships the same two words).
      return isOn(['present', 'presence', 'zone_1_presence']) ? tt('Present', 'Prisoten') : tt('Clear', 'Prosto');
    case 'synology': {
      // The body's own words per kind (SynologyWidget's healthy/error labels):
      // the NAS its `online`, a volume its `volume_error` then `disk_full`,
      // a disk its `disk_error`/`smart_ok`.
      if (synologyKind.value === 'nas') return isOn(['online']) ? on : off;
      if (synologyKind.value === 'volume') {
        if (isOn(['volume_error'])) return tt('Volume error', 'Napaka nosilca');
        if (isOn(['disk_full'])) return tt('Almost full', 'Skoraj poln');
        return tt('OK', 'OK');
      }
      return isOn(['disk_error']) || isOff(['smart_ok']) ? tt('Disk error', 'Napaka diska') : tt('OK', 'OK');
    }
    case 'vacuum':
    case 'lawn_mower': {
      // The two robots (card t_390bc438): the hub's own RVC state, then the
      // charge it is holding — the pair the sheet's own head shows. Empty when
      // the house has reported neither (an empty answer draws no pill).
      const pair = robotStateWord(robotState(deviceValues.value, liveValues.value));
      const word = pair ? tt(pair.en, pair.sl) : '';
      const battery = robotPercent(deviceValues.value, liveValues.value, ROBOT_BATTERY_KEY);
      if (!word) return battery === null ? '' : `${battery} %`;
      return battery === null ? word : `${word} · ${battery} %`;
    }
    case 'heat': {
      // The heat-demand controller's pill is its contact plus how many zones
      // are holding it — the numbers its own sheet leads with.
      const demand = isOn(['heat_demand']);
      const zones = toFiniteNumber(readValue(['active_count']));
      const word = demand ? tt('Heat demand', 'Potreba po toploti') : tt('No demand', 'Brez potrebe');
      return zones === null ? word : `${word} · ${Math.round(zones)}`;
    }
    default:
      return isOn(['power', 'on_off', 'state', 'switch', 'occupancy', 'online']) ? on : off;
  }
});

// Detect alarm active state — prefer live socket values for real-time reactivity
const hasAlarmActive = computed(() => {
  const prefixed = 'binary_sensor_alarm__alarm';
  if (prefixed in liveValues.value) {
    const v = liveValues.value[prefixed];
    return v === true || v === 1 || v === 'true';
  }
  if ('alarm' in liveValues.value) {
    const v = liveValues.value['alarm'];
    return v === true || v === 1 || v === 'true';
  }
  // Fall back to static prop data
  const alarmDv = deviceValues.value.find((v) => v.valueKey === 'binary_sensor_alarm__alarm' || v.valueKey === 'alarm');
  const v = alarmDv?.currentValue;
  return v === true || v === 1 || v === 'true';
});
</script>

<style scoped>
/* The chrome's body is this modal's only scrolling region: the modal hands it
   the flex fill and the overflow, the chrome itself never grows a scrollbar
   (its own note: "the host owns the scrolling container"). */
:deep(.device-sheet-body) {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}

/* The alarm flash and the accent ring are `DeviceSheetChrome`'s now (its
   `device-sheet--alarm` / `--active` treatment, colours and timing unchanged
   from this file's old `modal-alarm-flash`), so the alarm has exactly one owner
   across all three hosts. */
</style>
